import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiToast } from '../../hooks/useApiToast';
import './WithdrawalModal.css';

const WithdrawalModal = ({ isOpen, onClose, investment }) => {
  const { tokens } = useAuth();
  const { handleTokenOperation } = useApiToast();

  const [withdrawalType, setWithdrawalType] = useState('partial'); // 'partial' or 'full'
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalShares, setWithdrawalShares] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('amount'); // 'amount', 'confirm', 'processing', 'success'
  const [error, setError] = useState('');
  const [estimatedPayout, setEstimatedPayout] = useState(0);
  const [estimatedFees, setEstimatedFees] = useState(0);
  const [sharePrice, setSharePrice] = useState(1);
  const [poolInfo, setPoolInfo] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';

  useEffect(() => {
    if (isOpen && investment) {
      fetchPoolInfo();
      fetchSharePrice();
    }
  }, [isOpen, investment]);

  useEffect(() => {
    if (withdrawalType === 'full') {
      setWithdrawalShares(investment?.shares?.toString() || '');
      setWithdrawalAmount((investment?.shares * sharePrice)?.toFixed(6) || '');
    }
  }, [withdrawalType, investment, sharePrice]);

  useEffect(() => {
    if (withdrawalShares && sharePrice) {
      const shares = parseFloat(withdrawalShares);
      if (!isNaN(shares) && shares > 0) {
        const grossPayout = shares * sharePrice;
        setWithdrawalAmount(grossPayout.toFixed(6));
        calculateEstimatedPayout(shares, grossPayout);
      } else {
        setEstimatedPayout(0);
        setEstimatedFees(0);
      }
    }
  }, [withdrawalShares, sharePrice, poolInfo]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchPoolInfo = async () => {
    try {
      const data = await makeAuthenticatedRequest(`/investment/pools/${investment.pool_id}`);
      if (data.success) {
        setPoolInfo(data.data.pool);
      }
    } catch (error) {
      console.warn('Could not fetch pool info:', error.message);
    }
  };

  const fetchSharePrice = async () => {
    try {
      const data = await makeAuthenticatedRequest(`/investment/pools/${investment.pool_id}/share-price`);
      if (data.success) {
        setSharePrice(data.data.sharePrice || 1);
      }
    } catch (error) {
      console.warn('Could not fetch share price:', error.message);
      setSharePrice(1);
    }
  };

  const calculateEstimatedPayout = (shares, grossPayout) => {
    if (!poolInfo) {
      setEstimatedPayout(grossPayout);
      setEstimatedFees(0);
      return;
    }

    // Calculate management fees (prorated for time held)
    const managementFeeRate = (poolInfo.management_fee || 0) / 100;
    const daysHeld = Math.max(1, Math.floor((Date.now() - new Date(investment.investment_date)) / (1000 * 60 * 60 * 24)));
    const managementFee = (investment.amount_invested * managementFeeRate * daysHeld) / 365;

    // Calculate performance fees (only on gains)
    const performanceFeeRate = (poolInfo.performance_fee || 0) / 100;
    const gains = Math.max(0, grossPayout - investment.amount_invested * (shares / investment.shares));
    const performanceFee = gains * performanceFeeRate;

    const totalFees = managementFee + performanceFee;
    const netPayout = Math.max(0, grossPayout - totalFees);

    setEstimatedFees(totalFees);
    setEstimatedPayout(netPayout);
  };

  const validateWithdrawal = () => {
    const shares = parseFloat(withdrawalShares);
    
    if (!shares || shares <= 0) {
      setError('Please enter a valid number of shares to withdraw');
      return false;
    }

    if (shares > investment.shares) {
      setError('Cannot withdraw more shares than you own');
      return false;
    }

    if (poolInfo?.withdrawal_locked) {
      setError('Withdrawals are currently locked for this pool');
      return false;
    }

    setError('');
    return true;
  };

  const handleSharesChange = (e) => {
    const value = e.target.value;
    setWithdrawalShares(value);
    setError('');
  };

  const setMaxShares = () => {
    setWithdrawalShares(investment.shares.toString());
    setWithdrawalType('full');
  };

  const handleNext = () => {
    if (validateWithdrawal()) {
      setStep('confirm');
    }
  };

  const handleConfirm = async () => {
    if (!validateWithdrawal()) return;

    try {
      setLoading(true);
      setStep('processing');

      const withdrawalData = {
        investment_id: investment.id,
        shares_to_withdraw: parseFloat(withdrawalShares),
        withdrawal_type: withdrawalType
      };

      await handleTokenOperation(
        () => makeAuthenticatedRequest('/investment/withdraw', {
          method: 'POST',
          body: JSON.stringify(withdrawalData)
        }),
        {
          operationType: 'withdraw',
          successMessage: `Withdrawal request submitted for ${withdrawalShares} shares!`
        }
      );

      setStep('success');
      
      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      setError(error.message);
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('amount');
    setWithdrawalType('partial');
    setWithdrawalAmount('');
    setWithdrawalShares('');
    setError('');
    setEstimatedPayout(0);
    setEstimatedFees(0);
    onClose();
  };

  const formatNumber = (num) => {
    const numValue = parseFloat(num);
    if (isNaN(numValue) || numValue === 0) return '0.000';
    return numValue.toFixed(3);
  };

  const formatPercentage = (percent) => {
    const percentValue = parseFloat(percent);
    if (isNaN(percentValue) || percentValue === 0) return '0.00%';
    const formatted = percentValue.toFixed(2);
    return `${percentValue >= 0 ? '+' : ''}${formatted}%`;
  };

  if (!isOpen || !investment) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {step === 'amount' && 'Withdraw Investment'}
            {step === 'confirm' && 'Confirm Withdrawal'}
            {step === 'processing' && 'Processing Withdrawal'}
            {step === 'success' && 'Withdrawal Requested'}
          </h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Investment Information */}
          <div className="investment-info-summary">
            <h4>{investment.pool_name}</h4>
            <div className="investment-metrics-summary">
              <div className="metric">
                <span className="label">Total Shares:</span>
                <span className="value">{formatNumber(investment.shares)}</span>
              </div>
              <div className="metric">
                <span className="label">Current Value:</span>
                <span className="value">{formatNumber(investment.current_value)} SOL</span>
              </div>
              <div className="metric">
                <span className="label">Share Price:</span>
                <span className="value">{formatNumber(sharePrice)} SOL</span>
              </div>
            </div>
          </div>

          {/* Step: Amount Selection */}
          {step === 'amount' && (
            <div className="amount-step">
              <div className="withdrawal-type-selection">
                <label>Withdrawal Type:</label>
                <div className="type-options">
                  <label className="type-option">
                    <input
                      type="radio"
                      value="partial"
                      checked={withdrawalType === 'partial'}
                      onChange={(e) => setWithdrawalType(e.target.value)}
                    />
                    <span>Partial Withdrawal</span>
                  </label>
                  <label className="type-option">
                    <input
                      type="radio"
                      value="full"
                      checked={withdrawalType === 'full'}
                      onChange={(e) => setWithdrawalType(e.target.value)}
                    />
                    <span>Full Withdrawal</span>
                  </label>
                </div>
              </div>

              <div className="shares-input-section">
                <label htmlFor="withdrawal-shares">Shares to Withdraw</label>
                <div className="shares-input-container">
                  <input
                    id="withdrawal-shares"
                    type="number"
                    value={withdrawalShares}
                    onChange={handleSharesChange}
                    placeholder="0.000"
                    min="0"
                    max={investment.shares}
                    step="0.001"
                    className="shares-input"
                    disabled={withdrawalType === 'full'}
                  />
                  <button 
                    className="max-btn"
                    onClick={setMaxShares}
                    type="button"
                  >
                    MAX
                  </button>
                </div>
                
                <div className="shares-constraints">
                  <div className="constraint">
                    <span className="constraint-label">Available Shares:</span>
                    <span className="constraint-value">{formatNumber(investment.shares)}</span>
                  </div>
                  <div className="constraint">
                    <span className="constraint-label">Estimated SOL Value:</span>
                    <span className="constraint-value">{formatNumber(parseFloat(withdrawalAmount) || 0)} SOL</span>
                  </div>
                </div>
              </div>

              {withdrawalShares && estimatedPayout > 0 && (
                <div className="withdrawal-preview">
                  <h5>Withdrawal Preview</h5>
                  <div className="preview-metrics">
                    <div className="preview-metric">
                      <span className="label">Shares to Withdraw:</span>
                      <span className="value">{formatNumber(parseFloat(withdrawalShares))}</span>
                    </div>
                    <div className="preview-metric">
                      <span className="label">Gross Amount:</span>
                      <span className="value">{formatNumber(parseFloat(withdrawalAmount))} SOL</span>
                    </div>
                    <div className="preview-metric">
                      <span className="label">Estimated Fees:</span>
                      <span className="value negative">{formatNumber(estimatedFees)} SOL</span>
                    </div>
                    <div className="preview-metric total">
                      <span className="label">Net Payout:</span>
                      <span className="value">{formatNumber(estimatedPayout)} SOL</span>
                    </div>
                  </div>
                </div>
              )}

              {poolInfo?.withdrawal_notice_days > 0 && (
                <div className="withdrawal-notice">
                  <span className="notice-icon">ℹ️</span>
                  <span className="notice-text">
                    Note: This pool requires {poolInfo.withdrawal_notice_days} day notice. 
                    Your withdrawal will be processed after the notice period.
                  </span>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step: Confirmation */}
          {step === 'confirm' && (
            <div className="confirm-step">
              <div className="confirmation-details">
                <h5>Please confirm your withdrawal:</h5>
                
                <div className="confirmation-summary">
                  <div className="summary-row">
                    <span className="label">Pool:</span>
                    <span className="value">{investment.pool_name}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Withdrawal Type:</span>
                    <span className="value">{withdrawalType === 'full' ? 'Full Withdrawal' : 'Partial Withdrawal'}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Shares to Withdraw:</span>
                    <span className="value">{formatNumber(parseFloat(withdrawalShares))}</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Gross Amount:</span>
                    <span className="value">{formatNumber(parseFloat(withdrawalAmount))} SOL</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Estimated Fees:</span>
                    <span className="value negative">{formatNumber(estimatedFees)} SOL</span>
                  </div>
                  <div className="summary-row total">
                    <span className="label">Net Payout:</span>
                    <span className="value">{formatNumber(estimatedPayout)} SOL</span>
                  </div>
                </div>

                <div className="fee-breakdown">
                  <h6>Fee Breakdown:</h6>
                  <div className="fee-item">
                    <span className="fee-label">Management Fee:</span>
                    <span className="fee-value">{poolInfo?.management_fee || 0}% (prorated)</span>
                  </div>
                  <div className="fee-item">
                    <span className="fee-label">Performance Fee:</span>
                    <span className="fee-value">{poolInfo?.performance_fee || 0}% (on gains only)</span>
                  </div>
                </div>

                {poolInfo?.withdrawal_notice_days > 0 && (
                  <div className="processing-notice">
                    <span className="notice-icon">⏰</span>
                    <span className="notice-text">
                      Your withdrawal will be processed in {poolInfo.withdrawal_notice_days} days
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="processing-step">
              <div className="processing-animation">
                <div className="loading-spinner"></div>
                <h5>Processing Withdrawal Request</h5>
                <p>Please wait while we process your withdrawal request...</p>
              </div>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="success-step">
              <div className="success-animation">
                <div className="success-icon">✅</div>
                <h5>Withdrawal Request Submitted!</h5>
                <p>Your withdrawal request for {formatNumber(parseFloat(withdrawalShares))} shares has been submitted.</p>
                {poolInfo?.withdrawal_notice_days > 0 ? (
                  <p>It will be processed in {poolInfo.withdrawal_notice_days} days.</p>
                ) : (
                  <p>It will be processed shortly.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          {step === 'amount' && (
            <>
              <button className="cancel-btn" onClick={handleClose}>
                Cancel
              </button>
              <button 
                className="next-btn primary"
                onClick={handleNext}
                disabled={!withdrawalShares || parseFloat(withdrawalShares) <= 0}
              >
                Next
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button className="back-btn" onClick={() => setStep('amount')}>
                Back
              </button>
              <button 
                className="confirm-btn primary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Withdrawal'}
              </button>
            </>
          )}

          {step === 'processing' && (
            <button className="processing-btn" disabled>
              Processing...
            </button>
          )}

          {step === 'success' && (
            <button className="close-btn primary" onClick={handleClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawalModal;